import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import AuthService from "@/lib/auth";
import EmailService from "@/lib/email";
import { Mail, AlertTriangle, Loader2 } from "lucide-react";
import type { QuestionnaireResponseTypes } from "@/types/questionnaire";
import ENV from "@/lib/env";

interface OwnProps {
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  selectedResponses: QuestionnaireResponseTypes[];
  doctors: Array<{
    id: number | string;
    name: string;
    email: string;
    role?: string;
    role_type?: string;
  }>;
  // Added back to satisfy parent component passing it
  patientDoctorMap?: Map<string, any[]>;
  groupedPatients?: Record<string, {
    patient: QuestionnaireResponseTypes;
    responses: QuestionnaireResponseTypes[];
    doctors: any[];
  }>;
  isFetchingDoctors?: boolean;
}

interface BuildEmailParams {
  doctor_name: string;
  type: string;
  form_data: {
    first_name: string;
    last_name: string;
    date_of_birth: string;
    mobile_number: string;
  };
  total_score: number;
  questions: any[];
  graph_url: string;
  patient_id: string;
}

function buildEmailHtml(params: BuildEmailParams): string {
  // questions is used inside the helper function generateAllQuestionsHtml
  const { doctor_name, type, form_data, total_score, questions, graph_url, patient_id } = params;
  const patient_name = `${form_data.first_name} ${form_data.last_name}`.trim();

  function formatDOB(dateStr: string): string {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return `${String(date.getUTCMonth() + 1).padStart(2, "0")}-${String(date.getUTCDate()).padStart(2, "0")}-${date.getUTCFullYear()}`;
  }

  // Basic logic to show question results in email
  const questionsHtml = questions.length > 0
    ? questions.map((q: any, i: number) => `
        <p style="font-size:13px; margin-bottom: 10px;">
          <b style="color: #1e40af;">Q${i + 1}:</b> ${q.question || 'Assessment Item'}<br/>
          <span style="display: inline-block; margin-top: 4px; padding: 2px 8px; background: #dbeafe; color: #1e40af; border-radius: 12px; font-weight: bold; font-size: 12px;">
            Result: ${(q.selected_text || q.selected) ?? 'N/A'}
          </span>
          ${q.comment ? `<br/><i style="color: #4b5563; font-size: 12px;">Note: ${q.comment}</i>` : ''}
          ${q.voice_url ? `
            <br/><div style="margin-top: 6px;">
              <a href="${q.voice_url}" style="color: #2563eb; font-size: 12px; text-decoration: none; font-weight: bold; display: inline-flex; align-items: center;">
                <span style="margin-right: 4px;">🔊</span> Listen to Voice Response
              </a>
            </div>` : ''}
        </p>`).join('')
    : '<p>See dashboard for detailed responses.</p>';

  return `
    <div style="font-family:sans-serif;max-width:600px;margin:auto;border:1px solid #eee;padding:20px;">
      <h2 style="color:#2563eb;">${type} Assessment Result</h2>
      <p>Hi Dr. ${doctor_name},</p> <p><b>Patient:</b> ${patient_name}</p>
      <p><b>DOB:</b> ${formatDOB(form_data.date_of_birth)}</p>
      <hr/>
      <p><b>Total Score:</b> ${total_score}</p>
      <div style="background:#f9fafb;padding:10px;border-radius:4px;">${questionsHtml}</div>
      ${graph_url ? `<div style="margin-top:20px;"><img src="${graph_url}" width="100%"/></div>` : ''}
      <p style="text-align:center;margin-top:20px;">
        <a href="${(ENV.VITE_BASE_URL || "https://survey-dev.monitorhealth.ai").replace(/\/$/, "")}/patients/${patient_id}" style="background:#2563eb;color:#fff;padding:10px 20px;text-decoration:none;border-radius:5px;">Open Dashboard</a>
      </p>
    </div>`;
}

const EmailAction: React.FC<OwnProps> = ({
  modalOpen,
  setModalOpen,
  groupedPatients = {},
  isFetchingDoctors = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [ticketRaised, setTicketRaised] = useState(false);

  const uniquePatients = Object.keys(groupedPatients).length;
  const hasUnassignedPatients = Object.values(groupedPatients).some((d) => d.doctors.length === 0);
  const assignedResponsesCount = Object.values(groupedPatients)
    .filter((d) => d.doctors.length > 0)
    .reduce((sum, d) => sum + d.responses.length, 0);

  const raiseTicket = () => {
    setTicketRaised(true); // Using the setter to satisfy TS6133
    toast.info("Support ticket generated for unassigned patients.");
  };

  async function getMonthlyPHQChart(item: QuestionnaireResponseTypes): Promise<string> {
    try {
      const data = await AuthService.getQuestionnaireTrend(String(item.patient_id), item.questionnaire_type);
      if (!data || data.length === 0) return "";
      const chartConfig = {
        type: "line",
        data: {
          labels: data.map((r: any) => new Date(r.created_at).toLocaleDateString()),
          datasets: [{ label: "Score", data: data.map((r: any) => r.total_score), borderColor: "blue" }]
        }
      };
      return `https://quickchart.io/chart?c=${encodeURIComponent(JSON.stringify(chartConfig))}`;
    } catch { return ""; }
  }

  const sendMail = async () => {
    setIsLoading(true);
    let successCount = 0;

    for (const [, patientData] of Object.entries(groupedPatients)) {
      if (patientData.doctors.length === 0) continue;
      const assignedDoctor = patientData.doctors.find((d) => d.role_type === "Primary Provider") || patientData.doctors[0];

      for (const response of patientData.responses) {
        try {
          const chartUrl = await getMonthlyPHQChart(response);
          const parsedQuestions = typeof response.answers === 'string'
            ? JSON.parse(response.answers || "[]")
            : (response.answers || []);

          const emailHtml = buildEmailHtml({
            doctor_name: assignedDoctor.name,
            type: response.questionnaire_type,
            form_data: {
              first_name: response.patient?.first_name || response.patient_name.split(' ')[0],
              last_name: response.patient?.last_name || response.patient_name.split(' ').slice(1).join(' '),
              date_of_birth: response.date_of_birth,
              mobile_number: response.mobile_number || "N/A",
            },
            total_score: response.total_score || 0,
            questions: parsedQuestions,
            graph_url: chartUrl,
            patient_id: String(response.patient_id),
          });

          await EmailService.sendEmail({
            to: assignedDoctor.email,
            subject: `🩺 Review: ${response.questionnaire_type} for ${response.patient_name}`,
            body: emailHtml,
          });

          await AuthService.updateQuestionnaireResponse(String(response.id), { status: "email Submitted for Review" });
          successCount++;
        } catch (error) {
          console.error("Email processing failed", error);
        }
      }
    }

    if (successCount > 0) toast.success(`Sent ${successCount} reports`);
    setIsLoading(false);
    setModalOpen(false);
  };

  return (
    <Dialog open={modalOpen} onOpenChange={setModalOpen}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="text-[#1a7a4a]" /> 
            Patient Reports
          </DialogTitle>
        </DialogHeader>

        {isFetchingDoctors ? (
          <div className="py-20 flex flex-col items-center justify-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1a7a4a]"></div>
            <p className="text-gray-500 font-medium animate-pulse">Identifying assigned doctors...</p>
          </div>
        ) : (
          <div className="space-y-4 pt-4">
            <div className="bg-[#1a7a4a]/5 p-6 rounded-3xl border border-[#1a7a4a]/10">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#1a7a4a] animate-pulse" />
                  <span className="text-sm font-bold text-gray-700">
                    {uniquePatients} {uniquePatients === 1 ? 'Patient' : 'Patients'} Selected
                  </span>
                </div>
                <Badge variant="outline" className="bg-white border-[#1a7a4a]/20 text-[#1a7a4a] font-bold">
                  {assignedResponsesCount} Reports
                </Badge>
              </div>

              <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                {Object.values(groupedPatients).map(({ patient, doctors }, idx) => {
                  const assignedDoctor = doctors.find((d) => d.role_type === "Primary Provider") || doctors[0];
                  return (
                    <div key={idx} className="flex justify-between items-center p-3 rounded-2xl bg-white border border-gray-100 shadow-sm transition-all hover:shadow-md">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#1a7a4a]/10 flex items-center justify-center text-[#1a7a4a] font-bold text-xs">
                          {patient.patient_name?.[0]}
                        </div>
                        <span className="font-bold text-sm text-gray-900">{patient.patient_name}</span>
                      </div>
                      
                      {assignedDoctor ? (
                        <div className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-xl border border-gray-100">
                          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                          <span className="text-xs font-bold text-gray-600">Dr. {assignedDoctor.name}</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 bg-amber-50 px-3 py-1.5 rounded-xl border border-amber-100 text-amber-700">
                          <AlertTriangle className="w-3.5 h-3.5" />
                          <span className="text-xs font-bold uppercase tracking-tight">Unassigned</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {hasUnassignedPatients && !ticketRaised && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={raiseTicket} 
                className="w-full rounded-2xl border-amber-200 text-amber-700 hover:bg-amber-50 h-10 font-bold transition-all"
              >
                <AlertTriangle className="mr-2 h-4 w-4" /> 
                Raise Ticket for Missing Doctors
              </Button>
            )}

            <div className="flex gap-4 pt-2">
              <Button 
                variant="outline" 
                className="flex-1 h-12 rounded-2xl border-gray-200 text-gray-600 font-bold hover:bg-gray-50 transition-all" 
                onClick={() => setModalOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1 h-12 rounded-2xl bg-[#1a7a4a] hover:bg-[#15633c] text-white font-bold shadow-lg shadow-green-100 transition-all hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100" 
                onClick={sendMail} 
                disabled={isLoading || assignedResponsesCount === 0}
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Send {assignedResponsesCount} Reports
                  </div>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EmailAction;